import { Fragment, useState } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { AddonListings } from './AddonListings';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import { addonValidationSchema } from '@/utils/validations';
import { useCreateAddonMutation } from '@/services/AddonManagementService';
import { ErrorApiResponse } from '@/types/api.types';
import { IAddonData, PriceUnit } from '@/types/addon-management.types';
import { AddonModal } from './AddonModal';
import { usePermissions } from '@/hooks/usePermissions';

const AddonManagement = () => {
  const { currentLayout } = useLayout();
  const { hasPermission } = usePermissions();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<IAddonData>>({
    harbor_id: '',
    product_vat_config_id: null,
    name: '',
    description: '',
    price: undefined,
    price_unit: PriceUnit.PER_DAY,
    is_available: true,
    is_standalone: false,
    quantity: undefined,
    start_time: '',
    end_time: '',
    availability_date_ranges: [],
    access_handover_mode: 'NONE',
    language: 'en',
    translations: {},
    key_box_code: '',
    key_box_pickup_instructions: '',
    key_box_return_instructions: '',
    key_box_instruction_translations: {},
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { enqueueSnackbar } = useSnackbar();
  const [createAddon, { isLoading: isCreating }] = useCreateAddonMutation();

  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true);
    setFormData({
      harbor_id: '',
      product_vat_config_id: null,
      name: '',
      description: '',
      price: undefined,
      price_unit: PriceUnit.PER_DAY,
      is_available: true,
      is_standalone: false,
      quantity: undefined,
      start_time: '',
      end_time: '',
      harbor: { id: '', name: '' },
      access_handover_mode: 'NONE',
      language: 'en',
      translations: {},
      key_box_code: '',
      key_box_pickup_instructions: '',
      key_box_return_instructions: '',
      key_box_instruction_translations: {},
    });
    setErrors({});
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setFormData({});
    setErrors({});
  };

  const handleSubmit = async () => {
    try {
      await addonValidationSchema.validate(formData, { abortEarly: false });

      const payload = {
        ...formData,
        product_vat_config_id: formData.product_vat_config_id ?? null,
        price: formData.price ? Number(formData.price) : undefined,
        quantity: formData.quantity ? Number(formData.quantity) : undefined,
        availability_date_ranges: (
          formData.availability_date_ranges || []
        ).filter((range) => range.start_date && range.end_date),
      };

      await createAddon(payload).unwrap();
      enqueueSnackbar('Addon created successfully', { variant: 'success' });
      handleCloseCreateModal();
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) validationErrors[error.path] = error.message;
        });
        setErrors(validationErrors);
        enqueueSnackbar(
          `Please fix the form errors (${err.inner.length} errors occurred)`,
          { variant: 'error' }
        );
      } else {
        const errorMessage =
          (err as ErrorApiResponse).data?.message || 'Failed to create addon';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    }
  };

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle />
              <ToolbarDescription>
                You can manage your addons here.
              </ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              {hasPermission('harbor_addon_management_create') && (
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={handleOpenCreateModal}
                  aria-label="Add new addon"
                >
                  Add New Addon
                </button>
              )}
            </ToolbarActions>
          </Toolbar>
        </Container>
      )}
      <Container>
        <AddonListings />
      </Container>
      <AddonModal
        open={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        mode="create"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
        onSubmit={handleSubmit}
        isSubmitting={isCreating}
      />
    </Fragment>
  );
};

export { AddonManagement };
